import { create } from 'zustand';
import type { TerminalLine, TerminalState } from '@/types';

/**
 * Generate a unique ID for terminal lines.
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Terminal store for managing shell-like UI state.
 *
 * Tracks command history, typing state, and output lines.
 * Useful for interactive terminal-style elements.
 */
export const useTerminalStore = create<TerminalState>()((set) => ({
  history: [],
  isTyping: false,

  addLine: (content: string, type: TerminalLine['type']) => {
    const newLine: TerminalLine = {
      id: generateId(),
      content,
      type,
      timestamp: Date.now(),
    };

    set((state) => ({
      history: [...state.history, newLine],
    }));
  },

  clearHistory: () => {
    set({ history: [] });
  },

  setTyping: (isTyping: boolean) => {
    set({ isTyping });
  },
}));

/**
 * Selector hooks for performance optimization.
 * Use these to subscribe to specific slices of state.
 */
export const useTerminalHistory = () => useTerminalStore((state) => state.history);
export const useTerminalTyping = () => useTerminalStore((state) => state.isTyping);
